/**
 * Point matching for the drying graph's own hover tooltip.
 *
 * The graph disables Chart.js' tooltip (`plugins.tooltip.enabled: false`) and builds its own from the
 * nearest point of every visible dataset, so this is where "nearest" is defined.
 */

/** A plotted point as the chart holds it. `y` is null where the series has a gap. */
export interface PlottedPoint {
  x: number;
  y: number | null;
  /**
   * The value before the axis clamp, set by the extractor only when the clamp moved it.
   *
   * `y` is what gets drawn and has to stay inside the axis; this is what the reader is told. A ComBox slot
   * with no sensor reports −20 °C and a stuck one −3.5 °C, and reporting the clamped `y` made the tooltip
   * say "0 °C" for both, which reads as a real measurement rather than a broken sensor.
   */
  rawY?: number;
}

/** What the tooltip should report for a point: the pre-clamp value where there is one. */
export function displayValueOf(point: PlottedPoint): number | null {
  return point.rawY ?? point.y;
}

function hasValue(point: PlottedPoint | undefined): boolean {
  return point !== undefined && point.y !== null && point.y !== undefined;
}

/**
 * The nearest point of a series that actually carries a value, **at any distance**.
 *
 * No distance limit, deliberately. The limit used to be 2 % of the *visible* x range, which shrinks as
 * the reader zooms in while the sample spacing does not: at a half-hour zoom it comes to about 36
 * seconds, and ComBox samples are 10 to 30 minutes apart, so hovering produced no tooltip at all over
 * most of the graph. A value from further away, with the timestamp of the point it came from, is more
 * use than nothing.
 *
 * Gaps are skipped rather than treated as misses. A gap is plotted as `y: null`, and a binary search
 * that happens to land on one used to report "no point here" even though the series had values on both
 * sides.
 *
 * @param data Points sorted ascending by `x`, as the extractor produces them.
 * @param targetX Cursor position in graph units (minutes, hours or n-day steps).
 * @returns The closer of the two neighbours that have values, preferring the earlier one on a tie, or
 *   null when the series holds no value at all.
 */
export function findNearestPlottedPoint(
  data: PlottedPoint[],
  targetX: number
): PlottedPoint | null {
  // Insertion point: `hi` lands on the last x below the cursor, `lo` on the first at or above it.
  let lo = 0;
  let hi = data.length - 1;

  while (lo <= hi) {
    const mid = (lo + hi) >> 1;

    if (data[mid].x < targetX) {
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }

  let left = hi;
  let right = lo;

  while (left >= 0 && !hasValue(data[left])) {
    left--;
  }

  while (right < data.length && !hasValue(data[right])) {
    right++;
  }

  const before = left >= 0 ? data[left] : null;
  const after = right < data.length ? data[right] : null;

  if (!before) {
    return after;
  }

  if (!after) {
    return before;
  }

  return targetX - before.x <= after.x - targetX ? before : after;
}
